import React from 'react';
import { Mail, ExternalLink } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function InviteUserDialog({
  open,
  onOpenChange,
  inviteForm,
  setInviteForm,
  onSubmit,
  isSuperAdmin,
  organizations,
  onViewOrgSettings,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation email to add a new user
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium">Email Address</label>
            <Input
              type="email"
              placeholder="user@example.com"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Role</label>
            <Select
              value={inviteForm.role}
              onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
            >
              <SelectTrigger data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isSuperAdmin && (
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                )}
                <SelectItem value="org_admin">Organization Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isSuperAdmin ? (
            <div>
              <label className="text-sm font-medium">Organization</label>
              <Select
                value={inviteForm.organization_id}
                onValueChange={(v) => setInviteForm({ ...inviteForm, organization_id: v })}
              >
                <SelectTrigger data-testid="invite-org-select">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {organizations.map(org => (
                    <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            // Org admins always invite into their own org — clickable
            // breadcrumb that opens the Organizations tab if a
            // handler is supplied.
            <div>
              <label className="text-sm font-medium">Organization</label>
              <button
                type="button"
                onClick={onViewOrgSettings}
                disabled={!onViewOrgSettings}
                className={`mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-700 transition ${
                  onViewOrgSettings
                    ? 'hover:bg-slate-100 hover:border-slate-300 cursor-pointer'
                    : 'cursor-default'
                }`}
                data-testid="invite-org-readonly"
                title={onViewOrgSettings ? 'Open organization settings' : 'Scoped automatically to your org'}
              >
                <span className="font-medium">
                  {organizations.find(o => o.id === inviteForm.organization_id)?.name
                    || inviteForm.organization_id || 'Your organization'}
                </span>
                <span className="text-xs text-slate-500 ml-auto flex items-center gap-1">
                  scoped automatically
                  {onViewOrgSettings && <ExternalLink className="h-3 w-3" />}
                </span>
              </button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSubmit} data-testid="invite-submit-btn">
            <Mail size={16} className="mr-2" />
            Send Invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
